import {
  WSControl,
  WSControlGroup,
  WSElement,
  WSForm,
  WSInfoBox,
  WSInputNumber,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { RequirementExpirationStrategy } from "@wingspanhq/wingspan-api-sdk";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";

type FormSectionExpirationProps = {
  noExpirationForm: boolean;
};

export const FormSectionExpiration: React.FC<FormSectionExpirationProps> = ({
  noExpirationForm
}) => {
  const { register } = useFormContext();

  useEffect(() => {
    register("expirationStrategy");
    register("expirationDays");
  }, [register]);

  return (
    <WSPanel>
      <WSList gap="XL">
        <WSList gap="M">
          <WSText.ParagraphSm color="gray700">
            Expiration date
          </WSText.ParagraphSm>

          <WSInfoBox hidden={!noExpirationForm} theme="info">
            This requirement type will automatically extract the expiration date
            from the submitted document or file used to fulfill the requirement.
          </WSInfoBox>
          <WSForm.Field
            component={WSControlGroup}
            componentProps={{
              options: [
                {
                  helperText:
                    "The requirement will have no expiration date and will never expire.",
                  label: "Never expires",
                  value: RequirementExpirationStrategy.NONE
                },
                {
                  helperText:
                    "The requirement will expire after a fixed period after the date that the year starts.",
                  label: "Fixed period from year start",
                  value: RequirementExpirationStrategy.DAYS_AFTER_YEAR_START
                },
                {
                  helperText:
                    "The requirement will expire after a fixed period after the date that the requirement is completed.",
                  label: "Fixed period from completion",
                  value: RequirementExpirationStrategy.DAYS_AFTER_COMPLETE
                },
                {
                  helperText:
                    "The requirement will expire after a fixed period before the date that the year ends.",
                  label: "Fixed period before year end",
                  value: RequirementExpirationStrategy.DAYS_BEFORE_YEAR_END
                }
              ],
              size: "S",
              type: "radio"
            }}
            hidden={noExpirationForm}
            name="expirationStrategy"
          />
          <WSForm.Value name="expirationStrategy">
            {(expirationStrategy: RequirementExpirationStrategy) => (
              <WSInfoBox
                invisible={
                  ![
                    RequirementExpirationStrategy.DAYS_AFTER_YEAR_START,
                    RequirementExpirationStrategy.DAYS_BEFORE_YEAR_END,
                    RequirementExpirationStrategy.DAYS_AFTER_COMPLETE
                  ].includes(expirationStrategy) || noExpirationForm
                }
                theme="info"
              >
                <WSElement>
                  <WSForm.Field
                    component={WSInputNumber}
                    componentProps={{
                      format: true,
                      min: 1,
                      required: true
                    }}
                    label="Number of days"
                    name="expirationDays"
                  />
                </WSElement>
              </WSInfoBox>
            )}
          </WSForm.Value>
        </WSList>
        <WSList gap="M">
          <WSText.ParagraphSm color="gray700">
            Early renewals (optional)
          </WSText.ParagraphSm>
          <WSForm.Field
            component={WSControl}
            componentProps={{
              helperText:
                "We'll automatically remind contractors 30 days before an expiration date. \nCheck this box if you want to make renewals available anytime. \nAs the payer, you can also request renewals manually from the contractor's requirement detail page.",
              label:
                "Allow contractor to renew their documents for this requirement at any time",
              size: "S",
              type: "checkbox"
            }}
            name="manualRenewalAllowed"
          />
        </WSList>
      </WSList>
    </WSPanel>
  );
};
